import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import clsx from 'clsx'
import Swal from 'sweetalert2'
import {PageTitle} from '../../../_metronic/layout/core'
import type {
  ManualExamBookletLayoutSettings,
  ManualExamStatus,
} from '../my-course-exam/manual/core/_models'
import {
  createDefaultManualExamLayoutSettings,
  normalizeManualExamLayoutSettings,
} from '../my-course-exam/manual/core/_models'
import {
  downloadManualExamPdf,
  generateManualExamBooklets,
  updateManualExamStatus,
} from '../my-course-exam/manual/core/_requests'
import type {
  ExamQuestionAssignment,
  ExamQuestionAssignmentsResponse,
  ExamQuestionAssignmentOption,
} from '../my-course-exam/manual/core/_models'
import {getExamAssignments} from '../my-course-exam/manual/core/_requests'
import {
  importExamBookletMappings,
  updateExamBookletMappings,
} from '../exam-optical-reading/core/_requests'
import type {ExamBookletMappingResponse} from '../exam-optical-reading/core/_models'
import {
  getMeasurementCenterManualExamDetail,
  getMeasurementCenterManualExamSemesters,
  getMeasurementCenterManualExams,
  getMeasurementCenterManualExamTypes,
  deleteMeasurementCenterManualExam,
  bulkUpdateManualExamStatus,
} from './core/_requests'
import type {
  ManualExamMeasurementCenterDetail,
  ManualExamMeasurementCenterExamTypeOption,
  ManualExamMeasurementCenterListItem,
  ManualExamMeasurementCenterSemesterOption,
} from './core/_models'
import ManualExamLayoutSettingsEditor from '../my-course-exam/manual/ManualExamLayoutSettingsEditor'
import ManualExamLayoutPreview from '../my-course-exam/manual/ManualExamLayoutPreview'
import ManualExamSharedCoursesModal from './ManualExamSharedCoursesModal'
import SecureEExamSettingsPanel from './SecureEExamSettingsPanel'

const API_URL = process.env.REACT_APP_API_URL || ''

const STATUS_LABELS: Record<ManualExamStatus, string> = {
  Preparing: 'Hazırlanıyor',
  SentToMeasurementCenter: 'Ölçme Merkezine Gönderildi',
  PrintingInProgress: 'Basım Aşamasında',
  PrintingCompleted: 'Basım Tamamlandı',
  ExamCompleted: 'Sınav Tamamlandı',
  OpticalReadingCompleted: 'Optik Okuma Süreci Tamamlandı',
  GradingCompleted: 'Not Girişi Tamamlandı',
}

const STATUS_BADGES: Record<ManualExamStatus, string> = {
  Preparing: 'badge badge-light-secondary',
  SentToMeasurementCenter: 'badge badge-light-primary',
  PrintingInProgress: 'badge badge-light-info',
  PrintingCompleted: 'badge badge-light-success',
  ExamCompleted: 'badge badge-light-dark',
  OpticalReadingCompleted: 'badge badge-light-info',
  GradingCompleted: 'badge badge-light-success',
}

const DEFAULT_BOOKLET_CODES = ['A', 'B', 'C', 'D']
const MASTER_BOOKLET_CODE = '__MASTER__'

const clampBookletCount = (value?: number | null): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 1
  }

  const normalized = Math.floor(value)
  if (normalized < 1) {
    return 1
  }

  if (normalized > DEFAULT_BOOKLET_CODES.length) {
    return DEFAULT_BOOKLET_CODES.length
  }

  return normalized
}

const normalizeBookletCode = (value?: string | null) => {
  if (typeof value !== 'string') {
    return ''
  }

  return value.trim().toUpperCase()
}

const getLetterForIndex = (index: number) => String.fromCharCode(65 + index)

type BookletMappingModalState = {
  assignments: ExamQuestionAssignmentsResponse
  orders: Record<string, number[]>
  optionOrders: Record<string, Record<number, number[]>>
  activeCode: string | null
  selectedQuestionId: number | null
  uploadFile: File | null
  isSaving: boolean
  isImporting: boolean
}

const getAssignmentsQuestionList = (assignments?: ExamQuestionAssignmentsResponse | null) => {
  if (!assignments?.groups) {
    return []
  }

  return assignments.groups.flatMap(group => group.questions || [])
}

const buildBookletOrdersFromAssignments = (
  assignments: ExamQuestionAssignmentsResponse,
  fallbackCode: string = MASTER_BOOKLET_CODE
) => {
  const normalizedBookletCodes = (assignments.bookletCodes || []).map(code => normalizeBookletCode(code) || fallbackCode)
  const questionIds = getAssignmentsQuestionList(assignments).map(question => question.examQuestionId)
  const normalizedOrders: Record<string, number[]> = {}

  const rawOrders = assignments.bookletQuestionOrder ?? {}
  const availableCodes = new Set<string>([
    ...normalizedBookletCodes,
    ...Object.keys(rawOrders).map(code => normalizeBookletCode(code) || fallbackCode),
  ])

  if (availableCodes.size === 0 && questionIds.length > 0) {
    normalizedOrders[fallbackCode] = [...questionIds]
    return normalizedOrders
  }

  availableCodes.forEach(code => {
    const normalizedCode = code || fallbackCode
    const source =
      rawOrders[normalizedCode] ??
      rawOrders[normalizeBookletCode(code)] ??
      rawOrders[fallbackCode] ??
      []

    const sanitized = (source || []).filter(id => questionIds.includes(id))
    normalizedOrders[normalizedCode] = sanitized.length > 0 ? sanitized : [...questionIds]
  })

  return normalizedOrders
}

const buildInitialOptionOrders = (
  assignments: ExamQuestionAssignmentsResponse,
  bookletOrders: Record<string, number[]>,
  responseOptionOrders?: ExamBookletMappingResponse['optionOrderByBooklet']
): Record<string, Record<number, number[]>> => {
  const optionOrders: Record<string, Record<number, number[]>> = {}
  const questionMap = new Map<number, ExamQuestionAssignment>()
  getAssignmentsQuestionList(assignments).forEach(question => {
    questionMap.set(question.examQuestionId, question)
  })

  const allCodes = new Set<string>([
    ...Object.keys(bookletOrders),
    ...(assignments.bookletCodes || []).map(code => normalizeBookletCode(code) || MASTER_BOOKLET_CODE),
  ])

  allCodes.forEach(code => {
    const perQuestion: Record<number, number[]> = {}
    const order = bookletOrders[code] || []

    order.forEach(questionId => {
      const assignment = questionMap.get(questionId)
      const variantMap = assignment?.optionOrderByBooklet ?? {}
      const normalizedCode = normalizeBookletCode(code)
      const availableOptions: ExamQuestionAssignmentOption[] = assignment?.options || []
      const optionIds = availableOptions
        .map(option => (typeof option.id === 'number' ? (option.id as number) : null))
        .filter((id): id is number => Number.isFinite(id))

      const existing = responseOptionOrders?.[questionId]
      const candidate =
        existing?.[normalizedCode] ??
        existing?.[MASTER_BOOKLET_CODE] ??
        variantMap[normalizedCode] ??
        variantMap[MASTER_BOOKLET_CODE] ??
        assignment?.optionOrder ??
        optionIds

      perQuestion[questionId] = Array.from(new Set(candidate ?? optionIds))
    })

    optionOrders[code] = perQuestion
  })

  return optionOrders
}

const normalizeOptionOrderPayload = (optionOrders: Record<string, Record<number, number[]>>) => {
  const payload: Record<number, Record<string, number[]>> = {}

  Object.entries(optionOrders).forEach(([bookletCode, questionMap]) => {
    Object.entries(questionMap).forEach(([questionId, orders]) => {
      const parsedQuestionId = Number(questionId)
      if (!Number.isFinite(parsedQuestionId)) {
        return
      }

      if (!payload[parsedQuestionId]) {
        payload[parsedQuestionId] = {}
      }

      payload[parsedQuestionId][bookletCode] = orders
    })
  })

  return payload
}

const applyMappingResponse = (
  assignments: ExamQuestionAssignmentsResponse,
  response?: ExamBookletMappingResponse | null
) => {
  const mergedAssignments: ExamQuestionAssignmentsResponse = {
    ...assignments,
    bookletCodes: response?.bookletCodes ?? assignments.bookletCodes ?? [],
    bookletQuestionOrder: response?.bookletQuestionOrder ?? assignments.bookletQuestionOrder ?? {},
  }

  const orders = buildBookletOrdersFromAssignments(mergedAssignments)
  const optionOrders = buildInitialOptionOrders(mergedAssignments, orders, response?.optionOrderByBooklet)

  return {orders, optionOrders}
}

type ManualExamStatusFilter = ManualExamStatus | 'All'
type ManualExamStatusFilterValue = ManualExamStatusFilter | ''

const STATUS_FILTER_OPTIONS: {value: ManualExamStatusFilter; label: string}[] = [
  {value: 'SentToMeasurementCenter', label: STATUS_LABELS.SentToMeasurementCenter},
  {value: 'Preparing', label: STATUS_LABELS.Preparing},
  {value: 'PrintingInProgress', label: STATUS_LABELS.PrintingInProgress},
  {value: 'PrintingCompleted', label: STATUS_LABELS.PrintingCompleted},
  {value: 'ExamCompleted', label: STATUS_LABELS.ExamCompleted},
  {value: 'OpticalReadingCompleted', label: STATUS_LABELS.OpticalReadingCompleted},
  {value: 'GradingCompleted', label: STATUS_LABELS.GradingCompleted},
  {value: 'All', label: 'Tümü'},
]

const MANAGEMENT_STATUSES: ManualExamStatus[] = [
  'SentToMeasurementCenter',
  'PrintingInProgress',
  'PrintingCompleted',
  'ExamCompleted',
  'OpticalReadingCompleted',
  'GradingCompleted',
]

const BULK_STATUS_OPTIONS: ManualExamStatus[] = [
  'SentToMeasurementCenter',
  'PrintingInProgress',
  'PrintingCompleted',
  'ExamCompleted',
]

const isReturnedStatus = (
  status: ManualExamStatus,
  returnNote?: string | null,
  returnedAt?: string | null,
  returnedByName?: string | null
) => {
  if (status !== 'Preparing') {
    return false
  }

  if (returnNote && returnNote.trim().length > 0) {
    return true
  }

  if (returnedAt || returnedByName) {
    return true
  }

  return false
}

const getStatusDisplay = (
  status: ManualExamStatus,
  isReturned: boolean
): {label: string; badgeClass: string} => {
  if (isReturned) {
    return {label: 'İade Edildi', badgeClass: 'badge badge-light-warning'}
  }

  return {
    label: STATUS_LABELS[status] ?? status,
    badgeClass: STATUS_BADGES[status] ?? 'badge badge-light-primary',
  }
}

const formatCourseDisplay = (courseName?: string | null, courseCode?: string | null) => {
  const trimmedName = courseName?.trim()
  const trimmedCode = courseCode?.trim()

  if (trimmedName && trimmedCode) {
    return `${trimmedName} (${trimmedCode})`
  }

  if (trimmedName) {
    return trimmedName
  }

  if (trimmedCode) {
    return trimmedCode
  }

  return '-'
}

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '-'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleString()
}

const renderMultilineText = (value?: string | null) => {
  if (!value) {
    return null
  }

  const parts = value.split(/\r?\n/)
  return parts.map((part, index) => (
    <span key={`multiline-text-${index}`}>
      {part}
      {index < parts.length - 1 && <br />}
    </span>
  ))
}

type MeasurementCenterExamTypeGroup = {
  key: string
  baseName: string
  options: Array<{ id: number; fullName: string; order: number | null; orderLabel: string }>
}

const EXAM_TYPE_ORDER_REGEX = /^(\d+)\.\s*(.+)$/

const buildExamTypeGroups = (
  items: ManualExamMeasurementCenterExamTypeOption[]
): MeasurementCenterExamTypeGroup[] => {
  const groups = new Map<string, MeasurementCenterExamTypeGroup>()

  items.forEach((item, index) => {
    const rawName = (item?.name || '').trim()
    const matched = rawName.match(EXAM_TYPE_ORDER_REGEX)
    const order = matched ? Number(matched[1]) : null
    const baseName = (matched ? matched[2] : rawName).trim() || rawName || `Sınav Türü ${index + 1}`
    const key = baseName.toLocaleLowerCase('tr-TR')
    const orderLabel = matched ? `${matched[1]}.` : baseName

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        baseName,
        options: [],
      })
    }

    groups.get(key)?.options.push({
      id: item.id,
      fullName: rawName || baseName,
      order,
      orderLabel,
    })
  })

  return Array.from(groups.values()).map((group) => ({
    ...group,
    options: [...group.options].sort((a, b) => {
      if (a.order !== null && b.order !== null) {
        return a.order - b.order
      }
      if (a.order !== null) {
        return -1
      }
      if (b.order !== null) {
        return 1
      }
      return a.fullName.localeCompare(b.fullName, 'tr')
    }),
  }))
}

const MeasurementCenterManualExamsPage = () => {
  const [semesters, setSemesters] = useState<ManualExamMeasurementCenterSemesterOption[]>([])
  const [semestersLoading, setSemestersLoading] = useState(false)
  const [selectedSemesterId, setSelectedSemesterId] = useState<string>('')
  const [examTypes, setExamTypes] = useState<ManualExamMeasurementCenterExamTypeOption[]>([])
  const [examTypesLoading, setExamTypesLoading] = useState(false)
  const [selectedExamTypeGroupKey, setSelectedExamTypeGroupKey] = useState<string>('')
  const [selectedExamTypeId, setSelectedExamTypeId] = useState<string>('')
  const [list, setList] = useState<ManualExamMeasurementCenterListItem[]>([])
  const [listLoading, setListLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState<ManualExamStatusFilterValue>('')
  const [includeSubExams, setIncludeSubExams] = useState(false)
  const [selectedBulkExamIds, setSelectedBulkExamIds] = useState<number[]>([])
  const [bulkStatusSelection, setBulkStatusSelection] = useState<ManualExamStatus>('PrintingInProgress')
  const [bulkStatusUpdating, setBulkStatusUpdating] = useState(false)
  const [selectedExamId, setSelectedExamId] = useState<number | null>(null)
  const [detail, setDetail] = useState<ManualExamMeasurementCenterDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [statusSelection, setStatusSelection] = useState<ManualExamStatus>('SentToMeasurementCenter')
  const [statusUpdating, setStatusUpdating] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [questionSpacingLevel, setQuestionSpacingLevel] = useState(1)
  const [bookletCount, setBookletCount] = useState<number>(1)
  const [bookletGenerating, setBookletGenerating] = useState(false)
  const [layoutSettings, setLayoutSettings] = useState<ManualExamBookletLayoutSettings>(
    createDefaultManualExamLayoutSettings()
  )
  const [defaultLayoutSettings, setDefaultLayoutSettings] = useState<ManualExamBookletLayoutSettings | null>(null)
  const [isDynamicLayoutEnabled, setIsDynamicLayoutEnabled] = useState(false)
  const [isSharedModalOpen, setIsSharedModalOpen] = useState(false)
  const [bookletMappingModal, setBookletMappingModal] = useState<BookletMappingModalState | null>(null)
  const [bookletMappingLoading, setBookletMappingLoading] = useState(false)
  const listRequestIdRef = useRef(0)
  const groupedExamTypes = useMemo(() => buildExamTypeGroups(examTypes), [examTypes])
  const selectedExamTypeGroup = useMemo(
    () => groupedExamTypes.find((group) => group.key === selectedExamTypeGroupKey) || null,
    [groupedExamTypes, selectedExamTypeGroupKey]
  )

  const filtersReady = selectedSemesterId !== '' && selectedExamTypeId !== '' && statusFilter !== ''
  const selectableExamIds = useMemo(() => list.map((item) => item.examId), [list])
  const selectedBulkExamIdSet = useMemo(
    () => new Set(selectedBulkExamIds),
    [selectedBulkExamIds]
  )
  const allVisibleSelected =
    selectableExamIds.length > 0 && selectableExamIds.every((examId) => selectedBulkExamIdSet.has(examId))

  const filterInstructionMessage = useMemo(() => {
    if (filtersReady) {
      return null
    }

    if (semestersLoading) {
      return 'Dönem listesi yükleniyor.'
    }

    if (!selectedSemesterId) {
      return 'Lütfen dönem seçiniz.'
    }

    if (examTypesLoading) {
      return 'Sınav türleri yükleniyor.'
    }

    if (examTypes.length === 0) {
      return 'Seçilen döneme ait sınav türü bulunamadı.'
    }

    if (!selectedExamTypeId) {
      return 'Lütfen sınav türü seçiniz.'
    }

    if (!statusFilter) {
      return 'Lütfen durum seçiniz.'
    }

    return 'Lütfen filtreleri seçiniz.'
  }, [examTypes.length, examTypesLoading, filtersReady, selectedExamTypeId, selectedSemesterId, semestersLoading, statusFilter])

  useEffect(() => {
    const loadSemesters = async () => {
      try {
        setSemestersLoading(true)
        const data = await getMeasurementCenterManualExamSemesters()
        setSemesters(Array.isArray(data) ? data : [])
      } catch (err: any) {
        console.error(err)
        const message = err?.response?.data || 'Dönem listesi yüklenemedi.'
        Swal.fire('Hata', message, 'error')
        setSemesters([])
      } finally {
        setSemestersLoading(false)
      }
    }

    loadSemesters()
  }, [])

  useEffect(() => {
    setExamTypes([])
    setSelectedExamTypeGroupKey('')
    setSelectedExamTypeId('')
    setStatusFilter('')
    setList([])
    setSelectedExamId(null)
    setDetail(null)
    setExamTypesLoading(false)

    if (!selectedSemesterId) {
      return
    }

    const numericSemesterId = Number(selectedSemesterId)
    if (!Number.isFinite(numericSemesterId)) {
      return
    }

    let isMounted = true

    const loadExamTypes = async () => {
      try {
        setExamTypesLoading(true)
        const types = await getMeasurementCenterManualExamTypes(numericSemesterId)
        if (isMounted) {
          setExamTypes(types)
        }
      } catch (err: any) {
        console.error(err)
        if (isMounted) {
          const message = err?.response?.data || 'Sınav türleri yüklenemedi.'
          Swal.fire('Hata', message, 'error')
          setExamTypes([])
        }
      } finally {
        if (isMounted) {
          setExamTypesLoading(false)
        }
      }
    }

    loadExamTypes()

    return () => {
      isMounted = false
    }
  }, [selectedSemesterId])

  useEffect(() => {
    setList([])
    setSelectedExamId(null)
    setDetail(null)
  }, [selectedExamTypeId])

  useEffect(() => {
    if (!statusFilter) {
      setList([])
      setSelectedExamId(null)
      setDetail(null)
    }
  }, [statusFilter])

  useEffect(() => {
    setSelectedBulkExamIds((current) =>
      current.filter((examId) => selectableExamIds.includes(examId))
    )
  }, [selectableExamIds])

  useEffect(() => {
    setSelectedBulkExamIds([])
  }, [selectedSemesterId, selectedExamTypeId, statusFilter, includeSubExams])

  const loadList = useCallback(async () => {
    if (!filtersReady) {
      listRequestIdRef.current += 1
      setList([])
      setListLoading(false)
      return
    }

    const numericSemesterId = Number(selectedSemesterId)
    const numericExamTypeId = Number(selectedExamTypeId)

    if (!Number.isFinite(numericSemesterId) || !Number.isFinite(numericExamTypeId)) {
      listRequestIdRef.current += 1
      setList([])
      setListLoading(false)
      return
    }

    const requestId = listRequestIdRef.current + 1
    listRequestIdRef.current = requestId

    try {
      setListLoading(true)
      const exams = await getMeasurementCenterManualExams({
        semesterId: numericSemesterId,
        examTypeId: numericExamTypeId,
        status: statusFilter,
        includeSubExams,
      })
      if (listRequestIdRef.current !== requestId) {
        return
      }

      setList(exams)
    } catch (err: any) {
      if (listRequestIdRef.current !== requestId) {
        return
      }

      console.error(err)
      const message = err?.response?.data || 'Sınav listesi yüklenemedi.'
      Swal.fire('Hata', message, 'error')
      setList([])
    } finally {
      if (listRequestIdRef.current === requestId) {
        setListLoading(false)
      }
    }
  }, [filtersReady, includeSubExams, selectedExamTypeId, selectedSemesterId, statusFilter])

  const loadDetail = useCallback(async (examId: number) => {
    try {
      setDetailLoading(true)
      const examDetail = await getMeasurementCenterManualExamDetail(examId)
      setDetail(examDetail)
      if (examDetail) {
        setLayoutSettings(normalizeManualExamLayoutSettings(examDetail.layoutSettings))
        setIsDynamicLayoutEnabled(Boolean(examDetail.isDynamicLayoutEnabled))
        if (examDetail.defaultLayoutSettings) {
          setDefaultLayoutSettings(
            normalizeManualExamLayoutSettings(examDetail.defaultLayoutSettings)
          )
        } else {
          setDefaultLayoutSettings(null)
        }
      } else {
        setLayoutSettings(createDefaultManualExamLayoutSettings())
        setIsDynamicLayoutEnabled(false)
        setDefaultLayoutSettings(null)
      }
    } catch (err: any) {
      console.error(err)
      const message = err?.response?.data || 'Sınav detayı yüklenemedi.'
      Swal.fire('Hata', message, 'error')
      setDetail(null)
      setLayoutSettings(createDefaultManualExamLayoutSettings())
      setIsDynamicLayoutEnabled(false)
      setDefaultLayoutSettings(null)
    } finally {
      setDetailLoading(false)
    }
  }, [])

  useEffect(() => {
    loadList()
  }, [loadList])

  useEffect(() => {
    if (list.length === 0) {
      if (selectedExamId !== null) {
        setSelectedExamId(null)
      }
      setDetail((prevDetail) => (prevDetail !== null ? null : prevDetail))
      return
    }

    if (selectedExamId === null || !list.some((item) => item.examId === selectedExamId)) {
      setSelectedExamId(list[0].examId)
    }
  }, [list, selectedExamId])

  useEffect(() => {
    if (selectedExamId) {
      loadDetail(selectedExamId)
    }
  }, [selectedExamId, loadDetail])

  useEffect(() => {
    if (detail) {
      setStatusSelection(detail.status)
    }
  }, [detail])

  const hasSupplementaryMaterial = useMemo(() => {
    if (!detail?.supplementaryMaterialPath) {
      return false
    }
    return detail.supplementaryMaterialPath.trim().length > 0
  }, [detail?.supplementaryMaterialPath])

  const apiBaseUrlWithoutApi = useMemo(() => {
    return API_URL.replace(/\/api\/?$/, '')
  }, [])

  const supplementaryMaterialUrl = useMemo(() => {
    if (!detail?.supplementaryMaterialPath) {
      return ''
    }
    const trimmedPath = detail.supplementaryMaterialPath.trim()
    if (trimmedPath.startsWith('http')) {
      return trimmedPath
    }
    const normalizedPath = trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`
    return `${apiBaseUrlWithoutApi}${normalizedPath}`
  }, [detail?.supplementaryMaterialPath, apiBaseUrlWithoutApi])

  useEffect(() => {
    if (!detail) {
      setIsSharedModalOpen(false)
    }
  }, [detail])

  const downloadBaseName = useMemo(() => {
    if (!detail) {
      return 'manual_exam'
    }

    const parts = [detail.courseCode, detail.courseName, detail.examName]
      .map((part) => (part ?? '').trim())
      .filter((part) => part.length > 0)
      .map((part) => part.replace(/\s+/g, '_'))

    const joined = parts.length > 0 ? parts.join('_') : `manual_exam_${detail.examId}`
    return joined.replace(/[^A-Za-z0-9_-]+/g, '_')
  }, [detail])

  const handleSelectExam = useCallback((examId: number) => {
    setSelectedExamId(examId)
  }, [])

  const handleRefresh = useCallback(async () => {
    if (!filtersReady) {
      return
    }

    await loadList()
    if (selectedExamId) {
      await loadDetail(selectedExamId)
    }
  }, [filtersReady, loadList, loadDetail, selectedExamId])

  const handleToggleBulkExam = useCallback((examId: number) => {
    setSelectedBulkExamIds((current) =>
      current.includes(examId)
        ? current.filter((selectedId) => selectedId !== examId)
        : [...current, examId]
    )
  }, [])

  const handleToggleAllVisibleBulkExams = useCallback(() => {
    setSelectedBulkExamIds((current) => {
      const visibleIds = new Set(selectableExamIds)
      const currentWithoutVisible = current.filter((examId) => !visibleIds.has(examId))

      if (selectableExamIds.length > 0 && selectableExamIds.every((examId) => current.includes(examId))) {
        return currentWithoutVisible
      }

      return [...currentWithoutVisible, ...selectableExamIds]
    })
  }, [selectableExamIds])

  const handleBulkApplyStatus = useCallback(async () => {
    if (selectedBulkExamIds.length === 0) {
      Swal.fire('Uyarı', 'Lütfen en az bir sınav seçiniz.', 'warning')
      return
    }

    const targetStatusLabel = STATUS_LABELS[bulkStatusSelection] ?? bulkStatusSelection
    const result = await Swal.fire({
      title: 'Toplu Durum Güncelle',
      text: `${selectedBulkExamIds.length} sınavın durumu "${targetStatusLabel}" olarak güncellenecek.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Güncelle',
      cancelButtonText: 'Vazgeç',
    })

    if (!result.isConfirmed) {
      return
    }

    try {
      setBulkStatusUpdating(true)
      const response = await bulkUpdateManualExamStatus({
        examIds: selectedBulkExamIds,
        status: bulkStatusSelection,
      })

      const updatedCount = response?.updatedCount ?? 0
      const skippedCount = response?.skippedCount ?? 0
      const failedCount = response?.failedCount ?? 0
      const messageParts = [
        `${updatedCount} sınav güncellendi.`,
        skippedCount > 0 ? `${skippedCount} sınav zaten bu durumdaydı.` : null,
        failedCount > 0 ? `${failedCount} sınav güncellenemedi.` : null,
      ].filter(Boolean)

      await Swal.fire(
        failedCount > 0 ? 'Kısmi Tamamlandı' : 'Başarılı',
        messageParts.join(' '),
        failedCount > 0 ? 'warning' : 'success'
      )

      setSelectedBulkExamIds([])
    } catch (err: any) {
      console.error(err)
      const message = err?.response?.data || 'Toplu durum güncellemesi tamamlanamadı.'
      Swal.fire('Hata', message, 'error')
    } finally {
      setBulkStatusUpdating(false)
      await loadList()
      if (selectedExamId) {
        await loadDetail(selectedExamId)
      }
    }
  }, [bulkStatusSelection, loadDetail, loadList, selectedBulkExamIds, selectedExamId])

  const handleApplyStatus = useCallback(async () => {
    if (!detail) {
      return
    }

    const examId = detail.examId

    try {
      setStatusUpdating(true)
      await updateManualExamStatus({examId, status: statusSelection})
      Swal.fire('Başarılı', 'Sınav durumu güncellendi.', 'success')
    } catch (err: any) {
      console.error(err)
      const message = err?.response?.data || 'Sınav durumu güncellenemedi.'
      Swal.fire('Hata', message, 'error')
    } finally {
      setStatusUpdating(false)
      await loadList()
      await loadDetail(examId)
    }
  }, [detail, statusSelection, loadDetail, loadList])

  const handleReturnExam = useCallback(async () => {
    if (!detail) {
      return
    }

    const examId = detail.examId
    const result = await Swal.fire({
      title: 'Sınavı İade Et',
      input: 'textarea',
      inputLabel: 'İade açıklaması',
      inputPlaceholder: 'İade gerekçesini yazınız',
      showCancelButton: true,
      confirmButtonText: 'İade Et',
      cancelButtonText: 'Vazgeç',
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return 'İade açıklaması zorunludur.'
        }
        return undefined
      },
    })

    if (!result.isConfirmed) {
      return
    }

    const note = (result.value ?? '').trim()
    if (!note) {
      Swal.fire('Hata', 'İade açıklaması zorunludur.', 'error')
      return
    }

    try {
      setStatusUpdating(true)
      await updateManualExamStatus({examId, status: 'Preparing', returnNote: note})
      Swal.fire('Başarılı', 'Sınav iade edildi.', 'success')
    } catch (err: any) {
      console.error(err)
      const message = err?.response?.data || 'İade işlemi tamamlanamadı.'
      Swal.fire('Hata', message, 'error')
    } finally {
      setStatusUpdating(false)
      await loadList()
      await loadDetail(examId)
    }
  }, [detail, loadDetail, loadList])

  const handleOpenSharedModal = useCallback(() => {
    if (detail) {
      setIsSharedModalOpen(true)
    }
  }, [detail])

  const handleCloseSharedModal = useCallback(() => {
    setIsSharedModalOpen(false)
  }, [])

  const handleSharedModalChanged = useCallback(async () => {
    await loadList()
    if (selectedExamId) {
      await loadDetail(selectedExamId)
    }
  }, [loadDetail, loadList, selectedExamId])

  const handleDownload = useCallback(
    async (format: 'booklet' | 'sheet' | 'accessible') => {
      if (!detail) {
        return
      }

      try {
        setDownloading(true)
        const response = await downloadManualExamPdf(detail.examId, questionSpacingLevel, format)
        const isZip = format === 'booklet'
        const blob = new Blob([response.data], {
          type: isZip ? 'application/zip' : 'application/pdf',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        let fileName = `${downloadBaseName}_kitapciklar.zip`
        if (format === 'sheet') {
          fileName = `${downloadBaseName}_soru_kagidi.pdf`
        } else if (format === 'accessible') {
          fileName = `${downloadBaseName}_gorme_engelli_kitapcik.pdf`
        }
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (err: any) {
        console.error(err)
        const message = err?.response?.data || 'Dosya indirilemedi.'
        Swal.fire('Hata', message, 'error')
      } finally {
        setDownloading(false)
      }
    },
    [detail, downloadBaseName, questionSpacingLevel]
  )

  const handleGenerateBooklets = useCallback(async () => {
    if (!detail) {
      return
    }

    if (detail.status !== 'SentToMeasurementCenter') {
      Swal.fire('Uyarı', 'Kitapçık oluşturma işlemi yalnızca Ölçme Merkezine Gönderildi durumunda yapılabilir.', 'info')
      return
    }

    const examId = detail.examId
    const safeBookletCount = clampBookletCount(bookletCount)
    const shouldWarn = detail.hasBooklets

    if (shouldWarn) {
      const result = await Swal.fire({
        title: 'Kitapçıkları Yeniden Oluştur',
        text: 'Önceki kitapçıklar silinecektir. Basılı ve uygulanmış sınav varsa bu işlemi yapmayınız.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Evet, devam et',
        cancelButtonText: 'Vazgeç',
        reverseButtons: true,
      })

      if (!result.isConfirmed) {
        return
      }
    }

    try {
      setBookletGenerating(true)
      const layoutPayload = isDynamicLayoutEnabled
        ? normalizeManualExamLayoutSettings(layoutSettings)
        : undefined
      await generateManualExamBooklets({
        examId,
        bookletCount: safeBookletCount,
        layoutSettings: layoutPayload,
      })
      Swal.fire('Başarılı', 'Kitapçıklar oluşturuldu.', 'success')
    } catch (err: any) {
      console.error(err)
      const message = err?.response?.data || 'Kitapçıklar oluşturulamadı.'
      Swal.fire('Hata', message, 'error')
    } finally {
      setBookletGenerating(false)
      await loadList()
      await loadDetail(examId)
    }
  }, [bookletCount, detail, isDynamicLayoutEnabled, layoutSettings, loadDetail, loadList])

  const handleOpenBookletMappingModal = useCallback(async () => {
    if (!detail || bookletMappingLoading) {
      return
    }

    try {
      setBookletMappingLoading(true)
      const assignments = await getExamAssignments(detail.examId)
      const {orders, optionOrders} = applyMappingResponse(assignments)
      const codes = Object.keys(orders)
      const activeCode = codes.length > 0 ? codes[0] : null
      const selectedQuestionId = activeCode && orders[activeCode]?.length ? orders[activeCode][0] : null

      setBookletMappingModal({
        assignments,
        orders,
        optionOrders,
        activeCode,
        selectedQuestionId,
        uploadFile: null,
        isSaving: false,
        isImporting: false,
      })
    } catch (err: any) {
      console.error(err)
      const message = err?.response?.data || 'Kitapçık eşleştirmeleri yüklenemedi.'
      Swal.fire('Hata', message, 'error')
    } finally {
      setBookletMappingLoading(false)
    }
  }, [bookletMappingLoading, detail])

  const closeBookletMappingModal = useCallback(() => {
    setBookletMappingModal(null)
  }, [])

  const handleSelectBookletCode = useCallback((code: string) => {
    setBookletMappingModal(prev => {
      if (!prev) {
        return prev
      }

      const normalizedCode = normalizeBookletCode(code) || MASTER_BOOKLET_CODE
      const nextSelected = prev.orders[normalizedCode]?.[0] ?? prev.selectedQuestionId

      return {...prev, activeCode: normalizedCode, selectedQuestionId: nextSelected}
    })
  }, [])

  const handleSelectQuestion = useCallback((questionId: number) => {
    setBookletMappingModal(prev => (prev ? {...prev, selectedQuestionId: questionId} : prev))
  }, [])

  const moveBookletQuestion = useCallback((code: string, fromIndex: number, toIndex: number) => {
    setBookletMappingModal(prev => {
      if (!prev) {
        return prev
      }

      const list = prev.orders[code]
      if (!list || fromIndex < 0 || toIndex < 0 || fromIndex >= list.length || toIndex >= list.length) {
        return prev
      }

      const updated = [...list]
      const [moved] = updated.splice(fromIndex, 1)
      updated.splice(toIndex, 0, moved)

      return {
        ...prev,
        orders: {
          ...prev.orders,
          [code]: updated,
        },
      }
    })
  }, [])

  const moveBookletOption = useCallback((code: string, questionId: number, fromIndex: number, toIndex: number) => {
    setBookletMappingModal(prev => {
      if (!prev) {
        return prev
      }

      const perQuestion = prev.optionOrders[code] ?? {}
      const current = perQuestion[questionId]
      if (!current || fromIndex < 0 || toIndex < 0 || fromIndex >= current.length || toIndex >= current.length) {
        return prev
      }

      const reordered = [...current]
      const [moved] = reordered.splice(fromIndex, 1)
      reordered.splice(toIndex, 0, moved)

      return {
        ...prev,
        optionOrders: {
          ...prev.optionOrders,
          [code]: {
            ...perQuestion,
            [questionId]: reordered,
          },
        },
      }
    })
  }, [])

  const handleBookletUploadChange = useCallback((file?: File | null) => {
    setBookletMappingModal(prev => (prev ? {...prev, uploadFile: file ?? null} : prev))
  }, [])

  const handleImportBookletMapping = useCallback(async () => {
    if (!bookletMappingModal?.assignments || !bookletMappingModal.uploadFile) {
      Swal.fire('Uyarı', 'Lütfen excel dosyasını seçiniz.', 'warning')
      return
    }

    try {
      setBookletMappingModal(prev => (prev ? {...prev, isImporting: true} : prev))
      const response = await importExamBookletMappings(
        bookletMappingModal.assignments.examId,
        bookletMappingModal.uploadFile
      )
      const {orders, optionOrders} = applyMappingResponse(bookletMappingModal.assignments, response)

      setBookletMappingModal(prev =>
        prev
          ? {
              ...prev,
              orders,
              optionOrders,
              activeCode: Object.keys(orders)[0] ?? prev.activeCode,
              selectedQuestionId: Object.values(orders)[0]?.[0] ?? prev.selectedQuestionId,
              uploadFile: null,
              isImporting: false,
            }
          : prev
      )
      Swal.fire('Başarılı', 'Excel dosyasından kitapçık eşleştirmeleri güncellendi.', 'success')
    } catch (err: any) {
      console.error(err)
      const message = err?.response?.data?.message ?? 'Kitapçık eşleştirmeleri içe aktarılırken hata oluştu.'
      setBookletMappingModal(prev => (prev ? {...prev, isImporting: false} : prev))
      Swal.fire('Hata', message, 'error')
    }
  }, [bookletMappingModal])

  const handleSaveBookletMapping = useCallback(async () => {
    if (!bookletMappingModal?.assignments) {
      return
    }

    try {
      setBookletMappingModal(prev => (prev ? {...prev, isSaving: true} : prev))
      const response = await updateExamBookletMappings(
        bookletMappingModal.assignments.examId,
        bookletMappingModal.orders,
        normalizeOptionOrderPayload(bookletMappingModal.optionOrders)
      )
      const {orders, optionOrders} = applyMappingResponse(bookletMappingModal.assignments, response)

      setBookletMappingModal(prev =>
        prev
          ? {
              ...prev,
              orders,
              optionOrders,
              activeCode: prev.activeCode ?? Object.keys(orders)[0] ?? null,
              selectedQuestionId: prev.selectedQuestionId ?? Object.values(orders)[0]?.[0] ?? null,
              isSaving: false,
            }
          : prev
      )
      Swal.fire('Başarılı', 'Kitapçık eşleştirmeleri kaydedildi.', 'success')
    } catch (err: any) {
      console.error(err)
      const message = err?.response?.data?.message ?? 'Kitapçık eşleştirmeleri kaydedilirken hata oluştu.'
      setBookletMappingModal(prev => (prev ? {...prev, isSaving: false} : prev))
      Swal.fire('Hata', message, 'error')
    }
  }, [bookletMappingModal])

  const handleDeleteExam = useCallback(async () => {
    if (!detail) {
      return
    }

    const warningText = detail.isSubExam
      ? 'Birleşik sınavlarda silme işlemi sadece ana sınavdan yapılabilir.'
      : 'Bu sınav silinecektir. Birleşik sınav ise alt sınavların tamamı da silinir. Devam etmek istiyor musunuz?'

    const confirmation = await Swal.fire({
      title: 'Sınavı Sil',
      text: warningText,
      icon: detail.isSubExam ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonText: 'Evet, sil',
      cancelButtonText: 'Vazgeç',
      confirmButtonColor: '#d33',
      focusCancel: true,
    })

    if (!confirmation.isConfirmed) {
      return
    }

    if (detail.isSubExam) {
      return
    }

    try {
      await deleteMeasurementCenterManualExam(detail.examId)
      Swal.fire('Başarılı', 'Sınav silindi.', 'success')
      setDetail(null)
      setSelectedExamId(null)
      await loadList()
    } catch (err: any) {
      console.error(err)
      const message = err?.response?.data || err?.response?.data?.message || 'Sınav silinirken hata oluştu.'
      Swal.fire('Hata', message, 'error')
    }
  }, [detail, loadList])

  const statusOptions = useMemo(() => {
    if (!detail) {
      return MANAGEMENT_STATUSES
    }

    if (MANAGEMENT_STATUSES.includes(detail.status)) {
      return MANAGEMENT_STATUSES
    }

    return [detail.status, ...MANAGEMENT_STATUSES]
  }, [detail])

  const [isHistoryAccordionOpen, setIsHistoryAccordionOpen] = useState<boolean>(true)

  const measurementCenterReturnHistory = useMemo(() => {
    if (!detail?.measurementCenterReturnHistory || detail.measurementCenterReturnHistory.length === 0) {
      return []
    }

    const sorted = [...detail.measurementCenterReturnHistory]
    sorted.sort((a, b) => {
      const aTime = a?.returnedAt ? new Date(a.returnedAt).getTime() : 0
      const bTime = b?.returnedAt ? new Date(b.returnedAt).getTime() : 0
      return bTime - aTime
    })

    return sorted
  }, [detail?.measurementCenterReturnHistory])

  useEffect(() => {
    if (!detail) {
      setBookletCount(1)
      return
    }

    const existingCount = Array.isArray(detail.bookletCodes) ? detail.bookletCodes.length : 0
    const hasExistingBooklets = Boolean(detail.hasBooklets)

    if (hasExistingBooklets && existingCount > 0) {
      setBookletCount(clampBookletCount(existingCount))
      return
    }

    if (typeof detail.defaultBookletCount === 'number') {
      setBookletCount(clampBookletCount(detail.defaultBookletCount))
      return
    }

    if (existingCount > 0) {
      setBookletCount(clampBookletCount(existingCount))
      return
    }

    setBookletCount(1)
  }, [detail])

  const {generalRulesAccordionId, generalRulesHeadingId, generalRulesCollapseId} = useMemo(() => {
    const baseId = detail ? `generalExamRules-${detail.examId}` : 'generalExamRules'
    return {
      generalRulesAccordionId: baseId,
      generalRulesHeadingId: `${baseId}-heading`,
      generalRulesCollapseId: `${baseId}-collapse`,
    }
  }, [detail])

  const {historyAccordionId, historyHeadingId, historyCollapseId} = useMemo(() => {
    const baseId = detail ? `measurementCenterHistory-${detail.examId}` : 'measurementCenterHistory'
    return {
      historyAccordionId: baseId,
      historyHeadingId: `${baseId}-heading`,
      historyCollapseId: `${baseId}-collapse`,
    }
  }, [detail])

  const canGenerateBooklets = detail?.status === 'SentToMeasurementCenter'
  const canAdjustLayout = isDynamicLayoutEnabled && canGenerateBooklets
  const handleApplyDefaultLayout = useCallback(() => {
    if (!defaultLayoutSettings) {
      return
    }
    setLayoutSettings(normalizeManualExamLayoutSettings(defaultLayoutSettings))
  }, [defaultLayoutSettings])

  return (
    <>
      <PageTitle>Ölçme Merkezi Sınav Yönetimi</PageTitle>
      <div className='row g-5 g-xl-8'>
        <div className='col-lg-4'>
          <div className='card card-flush h-100'>
            <div className='card-header pt-5'>
              <h3 className='card-title'>Ölçme Merkezi Sınav Listesi</h3>
              <div className='card-toolbar d-flex flex-column align-items-stretch gap-3'>
                <label className='form-check form-check-sm form-check-custom form-check-solid mb-0'>
                  <input
                    id='include-sub-exams'
                    className='form-check-input'
                    type='checkbox'
                    checked={includeSubExams}
                    onChange={(event) => setIncludeSubExams(event.target.checked)}
                    disabled={listLoading}
                  />
                  <span className='form-check-label'>Alt sınavları göster</span>
                </label>
                <select
                  className='form-select form-select-sm'
                  value={selectedSemesterId}
                  onChange={(event) => setSelectedSemesterId(event.target.value)}
                  disabled={semestersLoading || listLoading}
                >
                  <option value=''>Dönem Seçiniz</option>
                  {semesters.map((semester) => (
                    <option key={semester.id} value={String(semester.id)}>
                      {semester.name?.trim() || `Dönem #${semester.id}`}
                      {semester.isActive === false ? ' (Pasif)' : ''}
                    </option>
                  ))}
                </select>
                <select
                  className='form-select form-select-sm'
                  value={selectedExamTypeGroupKey}
                  onChange={(event) => {
                    const groupKey = event.target.value
                    setSelectedExamTypeGroupKey(groupKey)
                    setStatusFilter('')
                    const selectedGroup = groupedExamTypes.find((group) => group.key === groupKey)
                    setSelectedExamTypeId(selectedGroup?.options[0] ? String(selectedGroup.options[0].id) : '')
                  }}
                  disabled={
                    selectedSemesterId === '' ||
                    examTypesLoading ||
                    listLoading ||
                    examTypes.length === 0
                  }
                >
                  <option value=''>
                    {selectedSemesterId === ''
                      ? 'Önce dönem seçiniz'
                      : examTypesLoading
                      ? 'Sınav türleri yükleniyor...'
                      : examTypes.length === 0
                      ? 'Sınav türü bulunamadı'
                      : 'Sınav Türü Seçiniz'}
                  </option>
                  {groupedExamTypes.map((group) => (
                    <option key={group.key} value={group.key}>
                      {group.baseName}
                    </option>
                  ))}
                </select>
                {selectedExamTypeGroup && selectedExamTypeGroup.options.length > 1 && (
                  <select
                    className='form-select form-select-sm'
                    value={selectedExamTypeId}
                    onChange={(event) => {
                      setSelectedExamTypeId(event.target.value)
                      setStatusFilter('')
                    }}
                    disabled={listLoading}
                  >
                    {selectedExamTypeGroup.options.map((option) => (
                      <option key={option.id} value={String(option.id)}>
                        {option.orderLabel}
                      </option>
                    ))}
                  </select>
                )}
                <select
                  className='form-select form-select-sm'
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value as ManualExamStatusFilterValue)}
                  disabled={selectedExamTypeId === '' || listLoading}
                >
                  <option value=''>Durum Seçiniz</option>
                  {STATUS_FILTER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary'
                  onClick={handleRefresh}
                  disabled={!filtersReady || listLoading || detailLoading}
                >
                  Yenile
                </button>
              </div>
            </div>
            <div className='card-body pt-5'>
              {filtersReady && list.length > 0 && (
                <div className='border rounded bg-light p-3 mb-4'>
                  <div className='d-flex flex-column gap-3'>
                    <div className='d-flex flex-wrap justify-content-between align-items-center gap-3'>
                      <label className='form-check form-check-sm form-check-custom form-check-solid mb-0'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          checked={allVisibleSelected}
                          onChange={handleToggleAllVisibleBulkExams}
                          disabled={bulkStatusUpdating || listLoading}
                        />
                        <span className='form-check-label fw-semibold'>
                          Görünen sınavları seç
                        </span>
                      </label>
                      <span className='text-muted small'>
                        Seçili sınav: {selectedBulkExamIds.length}
                      </span>
                    </div>
                    <div className='d-flex flex-column flex-xl-row gap-3'>
                      <select
                        className='form-select form-select-sm'
                        value={bulkStatusSelection}
                        onChange={(event) =>
                          setBulkStatusSelection(event.target.value as ManualExamStatus)
                        }
                        disabled={bulkStatusUpdating || listLoading}
                      >
                        {BULK_STATUS_OPTIONS.map((status) => (
                          <option key={status} value={status}>
                            {STATUS_LABELS[status] ?? status}
                          </option>
                        ))}
                      </select>
                      <button
                        type='button'
                        className='btn btn-sm btn-success'
                        onClick={handleBulkApplyStatus}
                        disabled={
                          selectedBulkExamIds.length === 0 ||
                          bulkStatusUpdating ||
                          listLoading ||
                          detailLoading
                        }
                      >
                        {bulkStatusUpdating ? 'Güncelleniyor...' : 'Seçilenleri Güncelle'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {!filtersReady ? (
                <div className='text-muted'>
                  {filterInstructionMessage ?? 'Lütfen filtreleri seçiniz.'}
                </div>
              ) : listLoading ? (
                <div className='text-center py-10'>
                  <span className='spinner-border text-primary' role='status' />
                </div>
              ) : list.length === 0 ? (
                <div className='text-muted'>Gösterilecek sınav bulunamadı.</div>
              ) : (
                <div
                  className='d-flex flex-column gap-4 pe-2'
                  style={{maxHeight: 'calc(100vh - 320px)', overflowY: 'auto'}}
                >
                  {list.map((item) => {
                    const isActive = item.examId === selectedExamId
                    const isReturned = isReturnedStatus(
                      item.status,
                      item.measurementCenterReturnNote,
                      item.measurementCenterReturnedAt,
                      item.measurementCenterReturnedByName
                    )
                    const {label: statusLabel, badgeClass} = getStatusDisplay(item.status, isReturned)

                    return (
                      <div
                        key={item.examId}
                        role='button'
                        tabIndex={0}
                        onClick={() => handleSelectExam(item.examId)}
                        onKeyDown={(evt) => {
                          if (evt.key === 'Enter' || evt.key === ' ') {
                            evt.preventDefault()
                            handleSelectExam(item.examId)
                          }
                        }}
                        className={clsx(
                          'border rounded p-4',
                          'd-flex flex-column gap-2',
                          'hover-elevate-up',
                          {'border-primary border-2 bg-light-primary': isActive}
                        )}
                      >
                        <div className='d-flex justify-content-between align-items-start gap-3'>
                          <div className='d-flex align-items-start gap-3 flex-grow-1'>
                            <input
                              className='form-check-input mt-1'
                              type='checkbox'
                              checked={selectedBulkExamIdSet.has(item.examId)}
                              onClick={(event) => event.stopPropagation()}
                              onChange={() => handleToggleBulkExam(item.examId)}
                              disabled={bulkStatusUpdating}
                            />
                            <div className='fw-bold text-gray-800'>
                              {formatCourseDisplay(item.courseName, item.courseCode)}
                            </div>
                          </div>
                          <span className={`${badgeClass} fw-semibold`}>{statusLabel}</span>
                        </div>
                        <div className='text-muted'>{item.examName ?? 'Sınav'}</div>
                        <div className='text-gray-700 small'>Sınav Türü: {item.examType?.trim() || 'Belirtilmemiş'}</div>
                        {item.instructorName && (
                          <div className='text-gray-700 small'>Gönderen: {item.instructorName}</div>
                        )}
                        <div className='d-flex flex-wrap gap-3 small text-gray-700'>
                          <div>Kitapçık: {item.bookletCount}</div>
                          <div>Oluşturma: {formatDateTime(item.bookletsGeneratedAt)}</div>
                        </div>
                        {item.measurementCenterSubmissionNote && (
                          <div className='alert alert-info py-2 px-3 mb-0'>
                            <div className='fw-semibold'>Gönderim Notu</div>
                            <div className='small text-gray-700'>
                              {renderMultilineText(item.measurementCenterSubmissionNote)}
                            </div>
                          </div>
                        )}
                        {item.measurementCenterReturnNote && (
                          <div className='alert alert-warning py-2 px-3 mb-0'>
                            <div className='fw-semibold'>İade Notu</div>
                            <div className='small text-gray-700'>
                              {renderMultilineText(item.measurementCenterReturnNote)}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='col-lg-8'>
          <div className='card h-100'>
            <div className='card-header pt-5'>
              <h3 className='card-title'>Sınav Detayları</h3>
            </div>
            <div className='card-body pt-5'>
              {detailLoading ? (
                <div className='text-center py-10'>
                  <span className='spinner-border text-primary' role='status' />
                </div>
              ) : !detail ? (
                <div className='text-muted'>Sınav seçiniz.</div>
              ) : (
                <div className='d-flex flex-column gap-6'>
                  {!detail.isMeasurementCenterActive && (
                    <div className='alert alert-warning'>
                      Ölçme merkezi ayarı pasif görünüyor. İşlemlerin yapılabilmesi için yönetici ile iletişime geçiniz.
                    </div>
                  )}
                  <div className='d-flex flex-column flex-md-row justify-content-between gap-3'>
                    <div>
                      <h2 className='fs-3 text-gray-900 mb-1'>{detail.examName ?? 'Sınav'}</h2>
                      <div className='text-muted'>
                        {formatCourseDisplay(detail.courseName, detail.courseCode)}
                      </div>
                      {detail.examType && <div className='text-muted'>Sınav Türü: {detail.examType}</div>}
                    </div>
                    <div className='d-flex flex-column align-items-md-end gap-2'>
                      <div>
                        {(() => {
                          const returned = isReturnedStatus(
                            detail.status,
                            detail.measurementCenterReturnNote,
                            detail.measurementCenterReturnedAt,
                            detail.measurementCenterReturnedByName
                          )
                          const {label, badgeClass} = getStatusDisplay(detail.status, returned)
                          return <span className={`${badgeClass} fw-semibold`}>{label}</span>
                        })()}
                      </div>
                      <div className='d-flex gap-2'>
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-danger'
                          onClick={handleDeleteExam}
                        >
                          Sınavı Sil
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-primary'
                          onClick={handleOpenSharedModal}
                        >
                          Ortak Sınav Girişi
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className='row g-3'>
                    <div className='col-md-6'>
                      <div className='fw-semibold text-gray-700'>Öğretim Elemanı</div>
                      <div className='text-gray-800'>{detail.instructorName ?? '-'}</div>
                      {detail.instructorEmail && (
                        <div className='text-muted small'>{detail.instructorEmail}</div>
                      )}
                    </div>
                    <div className='col-md-6'>
                      <div className='fw-semibold text-gray-700'>Sınav Süresi</div>
                      <div className='text-gray-800'>
                        {typeof detail.durationInMinutes === 'number' && Number.isFinite(detail.durationInMinutes)
                          ? `${detail.durationInMinutes} dakika`
                          : '-'}
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <div className='fw-semibold text-gray-700'>Öğrenci Sayısı</div>
                      <div className='text-gray-800'>
                        {typeof detail.studentCount === 'number' && Number.isFinite(detail.studentCount)
                          ? detail.studentCount
                          : '-'}
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <div className='fw-semibold text-gray-700'>Kitapçık Sayısı</div>
                      <div className='text-gray-800'>{detail.bookletCodes.length}</div>
                    </div>
                    <div className='col-md-6'>
                      <div className='fw-semibold text-gray-700'>Kitapçık Oluşturma</div>
                      <div className='text-gray-800'>{formatDateTime(detail.bookletsGeneratedAt)}</div>
                    </div>
                    </div>
                    {detail.bookletCodes.length > 0 && (
                      <div>
                        <div className='fw-semibold text-gray-700 mb-1'>Kitapçık Kodları</div>
                        <div className='d-flex flex-wrap gap-2'>
                          {detail.bookletCodes.map((code) => (
                            <span key={code} className='badge badge-light-primary fw-semibold'>
                              {code}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  {detail.instructions && (
                    <div>
                      <div className='fw-semibold text-gray-700 mb-1'>Talimatlar</div>
                      <div className='text-gray-800'>{detail.instructions}</div>
                    </div>
                  )}
                  <SecureEExamSettingsPanel
                    detail={detail}
                    onChanged={() => loadDetail(detail.examId)}
                  />
                  <div className='card bg-light border-0'>
                    <div className='card-body d-flex flex-column gap-3'>
                      <div className='fw-semibold text-gray-700 mb-1'>Ek Materyal (PDF)</div>
                      {hasSupplementaryMaterial ? (
                        <div className='d-flex flex-wrap align-items-center gap-3'>
                          <a
                            href={supplementaryMaterialUrl || '#'}
                            target='_blank'
                            rel='noreferrer'
                            className='fw-semibold text-primary text-decoration-underline'
                          >
                            {detail.supplementaryMaterialFileName || 'Ek Materyal'}
                          </a>
                          <span className='badge badge-light-success'>Hazır</span>
                          <div className='text-muted small'>
                            Öğretim elemanı tarafından yüklenen destek materyali buradan görüntülenebilir.
                          </div>
                        </div>
                      ) : (
                        <div className='text-muted'>Öğretim elemanı tarafından ek materyal yüklenmemiş.</div>
                      )}
                    </div>
                  </div>
                  {detail.measurementCenterSubmissionNote && (
                    <div className='alert alert-info'>
                      <div className='fw-semibold mb-1'>Gönderim Notu</div>
                      <div className='text-gray-800'>
                        {renderMultilineText(detail.measurementCenterSubmissionNote)}
                      </div>
                    </div>
                  )}
                  {detail.measurementCenterReturnNote && (
                    <div className='alert alert-warning'>
                      <div className='fw-semibold mb-1'>İade Açıklaması</div>
                      <div className='text-gray-800'>
                        {renderMultilineText(detail.measurementCenterReturnNote)}
                      </div>
                      <div className='small text-muted mt-2 d-flex flex-wrap gap-3'>
                        {detail.measurementCenterReturnedByName && (
                          <span>İade Eden: {detail.measurementCenterReturnedByName}</span>
                        )}
                        {detail.measurementCenterReturnedAt && (
                          <span>Tarih: {formatDateTime(detail.measurementCenterReturnedAt)}</span>
                        )}
                      </div>
                    </div>
                  )}
                  {measurementCenterReturnHistory.length > 0 && (
                    <div className='accordion' id={historyAccordionId}>
                      <div className='accordion-item border rounded'>
                        <h2 className='accordion-header' id={historyHeadingId}>
                          <button
                            className={clsx('accordion-button fw-semibold', !isHistoryAccordionOpen && 'collapsed')}
                            type='button'
                            onClick={() => setIsHistoryAccordionOpen((previous) => !previous)}
                            aria-expanded={isHistoryAccordionOpen}
                            aria-controls={historyCollapseId}
                          >
                            İşlem Tarihçesi
                          </button>
                        </h2>
                        <div
                          id={historyCollapseId}
                          className={clsx('accordion-collapse collapse', isHistoryAccordionOpen && 'show')}
                          aria-labelledby={historyHeadingId}
                          data-bs-parent={`#${historyAccordionId}`}
                        >
                          <div className='accordion-body d-flex flex-column gap-4'>
                            {measurementCenterReturnHistory.map((historyItem, index) => {
                            const actionType = historyItem?.actionType ?? null
                            const statusValue = historyItem?.status ?? null
                            const isHistoryReturn = actionType === 'Return'
                            const statusDisplay = statusValue
                              ? getStatusDisplay(statusValue, isHistoryReturn && statusValue === 'Preparing')
                              : null
                            const statusLabel: string | null = statusDisplay?.label ?? statusValue ?? null
                            const fallbackBadgeClass = statusValue
                              ? STATUS_BADGES[statusValue as ManualExamStatus] ?? 'badge badge-light-primary'
                              : null
                            const statusBadgeClass: string | null = statusDisplay?.badgeClass ?? fallbackBadgeClass
                            const badgeClassToUse = statusBadgeClass ?? 'badge badge-light-primary'
                            const historyTitle = actionType === 'StatusChange'
                              ? (statusLabel ?? 'Durum Güncellemesi')
                              : actionType === 'Return'
                              ? 'İade'
                              : 'İşlem'
                            const noteLines = historyItem?.note?.split(/\r?\n/) ?? []
                            const actorLabel = actionType === 'Return' ? 'İade Eden' : 'İşlemi Yapan'
                            const fallbackNote = actionType === 'Return'
                              ? 'Açıklama belirtilmemiş.'
                              : 'İşlem açıklaması bulunmuyor.'

                            return (
                              <div
                                key={`return-history-${index}`}
                                className='border rounded p-3 bg-light-primary bg-opacity-25 d-flex flex-column gap-3'
                              >
                                <div className='d-flex flex-wrap justify-content-between align-items-center gap-2'>
                                  <div className='fw-semibold text-gray-800'>{historyTitle}</div>
                                  {statusLabel && (
                                    <span className={`${badgeClassToUse} fw-semibold`}>{statusLabel}</span>
                                  )}
                                </div>
                                <div className='text-gray-800'>
                                  {noteLines.length > 0 ? (
                                    noteLines.map((line, lineIndex) => (
                                      <span key={`return-history-${index}-line-${lineIndex}`}>
                                        {line}
                                        {lineIndex < noteLines.length - 1 && <br />}
                                      </span>
                                    ))
                                  ) : (
                                    <span className='text-muted'>{fallbackNote}</span>
                                  )}
                                </div>
                                <div className='small text-muted d-flex flex-wrap gap-3'>
                                  <span>
                                    {actorLabel}: {historyItem?.returnedByName ?? '-'}
                                  </span>
                                  <span>Tarih: {formatDateTime(historyItem?.returnedAt)}</span>
                                </div>
                              </div>
                            )
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className='card bg-light border-0'>
                    <div className='card-body'>
                      <div className='row g-3 align-items-end'>
                        <div className='col-md-3'>
                          <label className='form-label'>Kitapçık Sayısı</label>
                          <select
                            className='form-select'
                            value={bookletCount}
                            onChange={(event) => {
                              const value = Number(event.target.value)
                              setBookletCount(clampBookletCount(value))
                            }}
                            disabled={bookletGenerating || !canGenerateBooklets}
                          >
                            {DEFAULT_BOOKLET_CODES.map((code, index) => (
                              <option key={code} value={index + 1}>
                                {index + 1}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className='col-md-3'>
                          <label className='form-label'>Soru Aralığı</label>
                          <select
                            className='form-select'
                            value={questionSpacingLevel}
                            onChange={(event) => {
                              const value = parseInt(event.target.value, 10)
                              if (!Number.isNaN(value)) {
                                const normalized = Math.min(5, Math.max(1, value))
                                setQuestionSpacingLevel(normalized)
                              }
                            }}
                          >
                            {[1, 2, 3, 4, 5].map((level) => (
                              <option key={level} value={level}>
                                {level}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className='col-lg-6 d-flex flex-column gap-3'>
                          <div className='d-flex flex-column flex-sm-row gap-3'>
                            <button
                              type='button'
                              className='btn btn-warning flex-grow-1'
                              disabled={bookletGenerating || !canGenerateBooklets}
                              onClick={handleGenerateBooklets}
                            >
                              {bookletGenerating ? 'İşleniyor...' : 'Kitapçıkları Oluştur'}
                            </button>
                            <button
                              type='button'
                              className='btn btn-light-primary flex-grow-1'
                              disabled={!detail || bookletMappingLoading}
                              onClick={handleOpenBookletMappingModal}
                            >
                              Kitapçık Eşleştirmelerini Yönet
                            </button>
                          </div>
                          <div className='row g-3'>
                            <div className='col-sm-6'>
                              <button
                                type='button'
                                className='btn btn-primary w-100'
                                disabled={downloading || !detail.hasBooklets}
                                onClick={() => handleDownload('booklet')}
                              >
                                PDF Paketini İndir
                              </button>
                            </div>
                            <div className='col-sm-6'>
                              <button
                                type='button'
                                className='btn btn-light w-100'
                                disabled={downloading || !detail.hasBooklets}
                                onClick={() => handleDownload('sheet')}
                              >
                                Soru Kağıdını İndir
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className='col-lg-6'>
                          <div className='border rounded-3 bg-white h-100 p-4 d-flex flex-column gap-3'>
                            <div>
                              <h6 className='fw-semibold mb-1'>Görme Engelli Kitapçığı</h6>
                              <p className='text-muted small mb-0'>
                                Görme engelli kitapçığı tek PDF olarak en az 17 punto yazı ile oluşturulur.
                              </p>
                            </div>
                            <button
                              type='button'
                              className='btn btn-outline-primary w-100'
                              disabled={downloading || !detail.hasBooklets}
                              onClick={() => handleDownload('accessible')}
                            >
                              Görme Engelli Kitapçığını İndir
                            </button>
                          </div>
                        </div>
                      </div>
                  </div>
                </div>
                {isDynamicLayoutEnabled && (
                  <div className='card border-0 bg-light mt-4'>
                    <div className='card-body'>
                      <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3'>
                        <h5 className='fw-semibold mb-0'>Kitapçık Tasarım Ayarları</h5>
                        {defaultLayoutSettings && (
                          <button
                            type='button'
                            className='btn btn-sm btn-light'
                            onClick={handleApplyDefaultLayout}
                            disabled={!canAdjustLayout}
                          >
                            Varsayılanı uygula
                          </button>
                        )}
                      </div>
                      <div className='row g-4'>
                        <div className='col-12'>
                          <ManualExamLayoutSettingsEditor
                            value={layoutSettings}
                            onChange={(next) =>
                              setLayoutSettings(normalizeManualExamLayoutSettings(next))
                            }
                            disabled={!canAdjustLayout}
                          />
                        </div>
                        <div className='col-12'>
                          <ManualExamLayoutPreview
                            value={layoutSettings}
                            className='h-100'
                            title='Önizleme'
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className='card border-dashed'>
                  <div className='card-body d-flex flex-column gap-4'>
                    <div className='d-flex flex-column flex-md-row align-items-md-end gap-3'>
                      <div className='flex-grow-1'>
                          <label className='form-label'>Durum Seçimi</label>
                          <select
                            className='form-select'
                            value={statusSelection}
                            onChange={(event) => setStatusSelection(event.target.value as ManualExamStatus)}
                            disabled={!detail.canManageStatus || statusUpdating}
                          >
                            {statusOptions.map((status) => (
                              <option key={status} value={status}>
                                {STATUS_LABELS[status] ?? status}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          type='button'
                          className='btn btn-success'
                          onClick={handleApplyStatus}
                          disabled={!detail.canManageStatus || statusUpdating}
                        >
                          Durumu Güncelle
                        </button>
                      </div>
                      <button
                        type='button'
                        className='btn btn-warning'
                        onClick={handleReturnExam}
                        disabled={!detail.canManageStatus || statusUpdating}
                      >
                        Sınavı İade Et
                      </button>
                    </div>
                  </div>
                </div>
              )}
      </div>
    </div>
  </div>
</div>
      {bookletMappingModal && (() => {
        const {assignments, orders, optionOrders, activeCode, selectedQuestionId} = bookletMappingModal
        const bookletLabels: Record<string, string> = {}
        const codeList = Object.keys(orders)
        ;(assignments.bookletCodes || []).forEach(code => {
          const normalized = normalizeBookletCode(code) || MASTER_BOOKLET_CODE
          bookletLabels[normalized] = code
        })

        const questionMap = new Map<number, ExamQuestionAssignment>()
        getAssignmentsQuestionList(assignments).forEach(question => {
          questionMap.set(question.examQuestionId, question)
        })

        const activeList = activeCode ? orders[activeCode] ?? [] : []
        const resolvedActive = activeCode ?? codeList[0] ?? null
        const selected = selectedQuestionId ? questionMap.get(selectedQuestionId) : undefined

        return (
          <>
            <div className='modal fade show d-block' tabIndex={-1} role='dialog'>
              <div className='modal-dialog modal-xl'>
                <div className='modal-content'>
                  <div className='modal-header'>
                    <h5 className='modal-title'>Kitapçık Eşleştirmelerini Yönet</h5>
                    <button type='button' className='btn-close' aria-label='Kapat' onClick={closeBookletMappingModal}></button>
                  </div>
                  <div className='modal-body'>
                    <div className='alert alert-primary d-flex align-items-center justify-content-between gap-3'>
                      <div>
                        <div className='fw-semibold mb-1'>Excel ile güncelle</div>
                        <div className='text-muted small'>Kitapçık sıralamasını içe aktarmak için excel dosyasını yükleyin.</div>
                      </div>
                      <div className='d-flex align-items-center gap-3'>
                        <input
                          type='file'
                          accept='.xls,.xlsx'
                          onChange={event => handleBookletUploadChange(event.target.files?.[0] ?? null)}
                          className='form-control'
                        />
                        <button
                          type='button'
                          className='btn btn-light-primary'
                          disabled={bookletMappingModal.isImporting}
                          onClick={handleImportBookletMapping}
                        >
                          {bookletMappingModal.isImporting ? 'Yükleniyor...' : 'İçe Aktar'}
                        </button>
                      </div>
                    </div>
                    <div className='row g-4'>
                      <div className='col-lg-6'>
                        <div className='mb-3 d-flex flex-wrap gap-2'>
                          {codeList.map(code => (
                            <button
                              key={code}
                              type='button'
                              className={clsx('btn btn-sm', resolvedActive === code ? 'btn-primary' : 'btn-light')}
                              onClick={() => handleSelectBookletCode(code)}
                            >
                              {bookletLabels[code] || code}
                            </button>
                          ))}
                        </div>
                        {resolvedActive && activeList.length > 0 ? (
                          <div className='border rounded-3 p-3 h-100'>
                            <div className='fw-semibold mb-2'>Soru sıralaması</div>
                            <div className='d-flex flex-column gap-2'>
                              {activeList.map((questionId, index) => {
                                const question = questionMap.get(questionId)
                                return (
                                  <div
                                    key={`${resolvedActive}-${questionId}`}
                                    className={clsx(
                                      'd-flex align-items-center justify-content-between border rounded p-2',
                                      selectedQuestionId === questionId ? 'border-primary' : 'border-gray-200'
                                    )}
                                  >
                                    <div className='d-flex flex-column'>
                                      <span className='fw-semibold'>Soru {index + 1}</span>
                                      <span
                                        className='text-muted image-wrapper'
                                        dangerouslySetInnerHTML={{__html: question?.questionName ?? 'Soru'}}
                                      ></span>
                                    </div>
                                    <div className='d-flex align-items-center gap-2'>
                                      <div className='btn-group btn-group-sm'>
                                        <button
                                          type='button'
                                          className='btn btn-light'
                                          disabled={index === 0}
                                          onClick={() => moveBookletQuestion(resolvedActive, index, index - 1)}
                                        >
                                          ↑
                                        </button>
                                        <button
                                          type='button'
                                          className='btn btn-light'
                                          disabled={index === activeList.length - 1}
                                          onClick={() => moveBookletQuestion(resolvedActive, index, index + 1)}
                                        >
                                          ↓
                                        </button>
                                      </div>
                                      <button
                                        type='button'
                                        className='btn btn-sm btn-outline-primary'
                                        onClick={() => handleSelectQuestion(questionId)}
                                      >
                                        Seç
                                      </button>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className='text-muted'>Kitapçık için soru sıralaması bulunamadı.</div>
                        )}
                      </div>
                      <div className='col-lg-6'>
                        <div className='border rounded-3 p-3 h-100'>
                          <div className='fw-semibold mb-2'>Seçenek sıralaması</div>
                          {resolvedActive && selected ? (
                            (() => {
                              const perQuestion = optionOrders[resolvedActive] || {}
                              const optionIds = perQuestion[selected.examQuestionId] || []
                              const options = selected.options || []
                              const optionLookup = new Map<number, ExamQuestionAssignmentOption>()
                              options.forEach(option => {
                                if (typeof option.id === 'number') {
                                  optionLookup.set(option.id as number, option)
                                }
                              })

                              const orderedOptions = optionIds
                                .map(optionId => optionLookup.get(optionId) || null)
                                .filter(Boolean) as ExamQuestionAssignmentOption[]

                              if (orderedOptions.length === 0) {
                                return <div className='text-muted'>Seçenek bulunamadı.</div>
                              }

                              return (
                                <div className='d-flex flex-column gap-2'>
                                  {orderedOptions.map((option, optionIndex) => (
                                    <div
                                      key={`${selected.examQuestionId}-${option.id ?? optionIndex}`}
                                      className='border rounded p-2 d-flex align-items-center justify-content-between'
                                    >
                                      <div>
                                        <div
                                          className='fw-semibold image-wrapper'
                                          dangerouslySetInnerHTML={{
                                            __html: `${getLetterForIndex(optionIndex)}. ${
                                              option.name ?? option.key ?? 'Seçenek'
                                            }`,
                                          }}
                                        ></div>
                                        {option.key && <div className='text-muted small'>{option.key}</div>}
                                      </div>
                                      <div className='btn-group btn-group-sm'>
                                        <button
                                          type='button'
                                          className='btn btn-light'
                                          disabled={optionIndex === 0}
                                          onClick={() =>
                                            moveBookletOption(resolvedActive, selected.examQuestionId, optionIndex, optionIndex - 1)
                                          }
                                        >
                                          ↑
                                        </button>
                                        <button
                                          type='button'
                                          className='btn btn-light'
                                          disabled={optionIndex === orderedOptions.length - 1}
                                          onClick={() =>
                                            moveBookletOption(resolvedActive, selected.examQuestionId, optionIndex, optionIndex + 1)
                                          }
                                        >
                                          ↓
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )
                            })()
                          ) : (
                            <div className='text-muted'>Seçenek düzenlemek için bir soru seçin.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='modal-footer'>
                    <button type='button' className='btn btn-light' onClick={closeBookletMappingModal}>
                      Kapat
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary'
                      disabled={bookletMappingModal.isSaving}
                      onClick={handleSaveBookletMapping}
                    >
                      {bookletMappingModal.isSaving ? 'Kaydediliyor...' : 'Sıralamayı Kaydet'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className='modal-backdrop fade show'></div>
          </>
        )
      })()}
      {detail && (
        <ManualExamSharedCoursesModal
          examId={detail.examId}
          isOpen={isSharedModalOpen}
          onClose={handleCloseSharedModal}
          onChanged={handleSharedModalChanged}
          statusLabels={STATUS_LABELS}
        />
      )}
    </>
  )
}

export default MeasurementCenterManualExamsPage
